/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";

// react-router components
import { useLocation, Link, useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import Breadcrumbs from "examples/Breadcrumbs";
import NotificationItem from "examples/Items/NotificationItem";
// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
  setDarkMode,
} from "context";
import { useDispatch, useSelector } from "react-redux";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";
import { Button, Divider, Tooltip, tooltipClasses } from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  Logout,
  GetNotification,
  UpdateNotification,
  MarkALLNotificationViewed,
  GetNotificationCount,
} from "../../../api-services";
import "./style.css";
import moment from "moment/moment";
function DashboardNavbar({ absolute = false, light = false, isMini = false }) {
  const [navbarType, setNavbarType] = useState();
  const [notificationCount, setNotificationCount] = useState(0);
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    transparentNavbar,
    fixedNavbar,
    openConfigurator,
    darkMode,
  } = controller;
  const { user_data } = useSelector((state) => state.authentication);
  const [openMenu, setOpenMenu] = useState(false);
  const route = useLocation().pathname.split("/").slice(1);
  const dispatchData = useDispatch();
  const navigate = useNavigate();
  const [notificationGet, setNotificationGet] = useState();

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(
        dispatch,
        (fixedNavbar && window.scrollY === 0) || !fixedNavbar
      );
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () =>
    setOpenConfigurator(dispatch, !openConfigurator);
  const handleOpenMenu = (event) => setOpenMenu(event.currentTarget);
  const handleCloseMenu = () => setOpenMenu(false);
  const handleLogout = () => Logout(dispatchData, navigate);
  const handleDarkMode = () => setDarkMode(dispatch, !darkMode);

  // Tooltip for Menu
  const CustomWidthTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))({
    [`& .${tooltipClasses.tooltip}`]: {
      fontSize: "12px",
      fontFamily: "inherit",
      textAlign: "left",
      fontWeight: "bold",
      background: "#a3a6ae",
      color: "#000",
    },
  });

  useEffect(() => {
    const interval = setInterval(() => {
      GetNotificationCount(successActions, () => {}); // Notification Count
      GetNotification(successAction, () => {}); // Notification Fetch
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    GetNotification(successAction, () => {}); // Notification Fetch
    GetNotificationCount(successActions, () => {}); // Notification Count
  }, []);

  // Notification Count
  const successActions = (res) => {
    const getData = res?.data;
    setNotificationCount(getData);
  };

  const HandleNotificationClick = (redirect, getNotify) => {
    if (redirect === "Sandbox" || redirect === "Registration") {
      navigate("/users", { state: { email: getNotify } });
    } else if (redirect === "Production") {
      navigate("/production-requests", { state: { email: getNotify } });
    } else if (redirect === "Support") {
      navigate("/support", { state: { email: getNotify } });
    } else if (redirect === "API Questionnaire") {
      navigate("/tech-requests", { state: { email: getNotify } });
    }
  };
  // Notification Get
  const successAction = (res) => {
    if (res?.data?.length > 0) {
      const getData = res?.data;
      const getMessage = getData.map(function (val, index) {
        const fullName = val.fullName;
        const getAction = val.area;
        const getDesc = val.message;
        const isRead = val.isRead;
        const notificationId = val.notificationId;
        const notificationFrom = val.notificationFrom;
        const getDate = moment(val.createdOn).format("LLL");
        const mergeNotification = (
          <MDTypography sx={{ fontSize: "14px", fontWeight: "bold" }}>
            {fullName ? fullName + ":" : ""} {getDesc}
            <br />
            <MDTypography sx={{ fontSize: "12px", fontWeight: "normal" }}>
              {getDate}
            </MDTypography>
          </MDTypography>
        );

        const getIcon = (action) => {
          switch (action) {
            case "Registration":
              return <Icon>group</Icon>;
            case "Sandbox":
              return <Icon>add_chart</Icon>;
            case "Production":
              return <Icon>request_quote_icon</Icon>;
            case "Support":
              return <Icon>support_agent</Icon>;
            default:
              return null;
          }
        };
        const notifyComponent = (
          <MDBox>
            <NotificationItem
              icon={getIcon(getAction)}
              className={isRead ? "read" : "unread"}
              title={mergeNotification}
              onClick={
                isRead
                  ? () => {
                      if (user_data?.customer?.roleId !== 1) {
                        HandleNotificationClick(getAction, notificationFrom);
                      } else {
                        return undefined;
                      }
                    }
                  : () => {
                      if (user_data?.customer?.roleId !== 1) {
                        HandleNotificationClick(getAction, notificationFrom);
                      }
                      setNotificationGet(getMessage);
                      UpdateNotification(
                        dispatchData,
                        { notificationId },
                        () => {}
                      );
                    }
              }
            />
            <Divider
              orientation="horizontal"
              flexItem
              sx={{ height: "1px", opacity: "0.8" }}
            />
          </MDBox>
        );

        return notifyComponent;
      });
      //Set State
      setNotificationGet(getMessage);
    } else {
      setNotificationGet(
        <NotificationItem
          className={darkMode ? "darkmode" : "lightmode"}
          icon={<Icon>notifications_off</Icon>}
          title="No Notification Found!"
        />
      ); //Set State
    }
  };

  // Render the notifications menu
  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{ mt: 2, maxHeight: "600px" }}
      className={darkMode ? "darkmode" : "lightmode"}
    >
      <MDBox
        sx={{
          display: "flex",
          justifyContent: "space-between",
          margin: "0 15px 0 15px",
        }}
      >
        <MDTypography
          sx={{
            fontSize: "16px",
            fontWeight: "bold",
            lineHeight: "30px",
            marginRight: "20px",
          }}
        >
          Notifications
        </MDTypography>
        <Button
          size="small"
          onClick={() => {
            MarkALLNotificationViewed();
          }}
        >
          Mark all as read
        </Button>
      </MDBox>
      <Divider
        orientation="horizontal"
        flexItem
        sx={{ height: "1px", opacity: "0.8" }}
      />
      {notificationGet}
    </Menu>
  );

  // Styles for the navbar icons
  const iconsStyle = ({
    palette: { dark, white, text },
    functions: { rgba },
  }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;

      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }

      return colorValue;
    },
  });

  const firstName = user_data?.customer?.firstName;
  const lastName =
    user_data?.customer?.lastName !== undefined &&
    user_data?.customer?.lastName !== null
      ? user_data?.customer?.lastName
      : "";
  const fullName = firstName + " " + lastName;

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) =>
        navbar(theme, { transparentNavbar, absolute, light, darkMode })
      }
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <MDBox
          color="inherit"
          mb={{ xs: 1, md: 0 }}
          sx={(theme) => navbarRow(theme, { isMini })}
          className="nav-top"
        >
          <Breadcrumbs
            icon="home"
            title={route[route.length - 1]}
            route={route}
            light={light}
          />
        </MDBox>
        {isMini ? null : (
          <MDBox sx={(theme) => navbarRow(theme, { isMini })}>
            {/* <MDBox pr={1}>
              <MDInput label="Search here" />
            </MDBox> */}
            <MDBox
              color={light ? "white" : "inherit"}
              display="flex"
              flexDirection="row"
            >
              <MDBox display="flex" alignItems="center">
                <CustomWidthTooltip title={fullName}>
                  <MDTypography
                    className={
                      user_data?.customer?.roleId != 1
                        ? "admin-title"
                        : "username-title"
                    }
                    textTransform="uppercase"
                    fontSize="0.875rem"
                    color="text"
                    pt={0.5}
                  >
                    {fullName}
                  </MDTypography>
                </CustomWidthTooltip>
                {user_data?.customer?.roleId != 1 && (
                  <MDBadge
                    badgeContent={user_data?.customer?.role}
                    color="info"
                    size="lg"
                    sx={{ "& .MuiBadge-badge": { background: "#022DEC", backgroundImage: "none" } }}
                  />
                )}
              </MDBox>
              <Divider
                orientation="vertical"
                flexItem
                sx={{ height: "32px", opacity: "0.8", marginRight: "5px" }}
              />
              {/* <CustomWidthTooltip
                title={darkMode ? "Toggle Light Mode" : "Toggle Dark Mode"}
              >
                <IconButton
                  size="small"
                  disableRipple
                  color="inherit"
                  sx={navbarIconButton}
                  onClick={handleDarkMode}
                >
                  <Icon sx={iconsStyle} fontSize="medium">
                    {darkMode ? "light_mode" : "dark_mode"}
                  </Icon>
                </IconButton>
              </CustomWidthTooltip> */}
              <CustomWidthTooltip title="Profile">
                <Link to="/profile">
                  <IconButton sx={navbarIconButton} size="small" disableRipple>
                    <Icon sx={iconsStyle}>account_circle</Icon>
                  </IconButton>
                </Link>
              </CustomWidthTooltip>
              <CustomWidthTooltip title="Toggle Menu">
                <IconButton
                  size="small"
                  disableRipple
                  color="inherit"
                  sx={navbarMobileMenu}
                  onClick={handleMiniSidenav}
                >
                  <Icon sx={iconsStyle} fontSize="medium">
                    menu
                  </Icon>
                </IconButton>
              </CustomWidthTooltip>
              {/* <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                onClick={handleConfiguratorOpen}
              >
                <Icon sx={iconsStyle}>settings</Icon>
              </IconButton> */}

              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                aria-controls="notification-menu"
                aria-haspopup="true"
                variant="contained"
                onClick={handleOpenMenu}
              >
                <CustomWidthTooltip title="Toggle Notifications">
                  <Icon sx={iconsStyle}>notifications</Icon>
                </CustomWidthTooltip>
                {notificationCount > 0 ? (
                  <p className="count-notify">
                    {notificationCount > 100 ? "99+" : notificationCount}
                  </p>
                ) : (
                  ""
                )}
              </IconButton>
              {renderMenu()}

              <CustomWidthTooltip title="Logout">
                <IconButton
                  size="small"
                  disableRipple
                  color="inherit"
                  sx={navbarIconButton}
                  variant="contained"
                  onClick={handleLogout}
                >
                  <Icon sx={iconsStyle}>logout</Icon>
                </IconButton>
              </CustomWidthTooltip>
            </MDBox>
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
